import { useEffect, useRef } from "react";
import { useNavigationType, useSearchParams } from "react-router-dom";

/** One-shot search-param read for useState initializers. */
export function urlParam(key: string): string | null {
  return new URLSearchParams(window.location.search).get(key);
}

/**
 * Two-way binding between a page's view state and the URL search params, so
 * the address bar always describes the current view and a pasted link
 * restores it.
 *
 * `values` maps param name → current state, where "" means "not set" and
 * removes the param. When the params change (history navigation), entries
 * that differ from the state are handed to `apply` in one batch — appliers
 * must treat "" as "back to default". When the state changes, the URL is
 * rewritten in place (`replace`, so view tweaks don't grow the back stack).
 * On the first render only params the URL is missing are filled in — state
 * seeded from the URL via `urlParam` already matches the rest.
 */
export function useUrlState(
  values: Record<string, string>,
  apply: (incoming: Record<string, string>) => void,
): void {
  const [params, setParams] = useSearchParams();
  const navigationType = useNavigationType();
  const latest = useRef({ values, apply });
  latest.current = { values, apply };
  const mounted = useRef(false);

  // URL → state, but only on history traversal (back/forward). Skipped on
  // mount (initializers read the URL) and for PUSH/REPLACE — those are our
  // own writes below, and applying them back would ping-pong stale values
  // into state in an endless loop while several state updates settle.
  useEffect(() => {
    if (!mounted.current || navigationType !== "POP") return;
    const { values, apply } = latest.current;
    const incoming: Record<string, string> = {};
    for (const key of Object.keys(values)) {
      const value = params.get(key) ?? "";
      if (value !== values[key]) incoming[key] = value;
    }
    if (Object.keys(incoming).length > 0) apply(incoming);
  }, [params, navigationType]);

  // State → URL.
  const signature = JSON.stringify(values);
  useEffect(() => {
    const isFirst = !mounted.current;
    mounted.current = true;
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(latest.current.values)) {
          if (isFirst && prev.get(key) !== null) continue;
          if (value) next.set(key, value);
          else next.delete(key);
        }
        return next;
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);
}
